import { domainCopy } from '../copy';
import type { Language } from '../types/language';
import { isCompanyEmail } from '../types/staffSignIn';
import type { ContentTeamRole } from '../types/questAdmin';
import type { ISODate } from '../types/ids';
import { isoDateIn } from './time';

/**
 * **THE CONTENT TEAM'S SIX ROLES** (Evan, 2026-09-26), replacing Owner and Editor:
 *
 * - **Owner** manages the team (and can do everything a Publisher can).
 * - **Publisher** reviews and publishes.
 * - **Writer** edits drafts but can't publish.
 * - **Reviewer** reads drafts, comments and approves, but can't edit or publish.
 * - **Icon designer** uploads icons only.
 * - **Viewer** sees drafts and history and changes nothing.
 *
 * Only company accounts can be Owner or Publisher. Every role is stored with a SCOPE — only "all Kredit Academy
 * content" for now; partner lessons are always reviewed and published by Kredit Academy.
 */
export const CONTENT_TEAM_ROLES = [
  'owner',
  'publisher',
  'writer',
  'reviewer',
  'iconDesigner',
  'viewer',
] as const;

/** What a role is asked for on a request. `review` is a script sign-off (approval); `comment` is a comment on a draft. */
export type ContentAct = 'read' | 'comment' | 'review' | 'edit' | 'publish' | 'icons' | 'team';

const ACTS: Record<ContentTeamRole, readonly ContentAct[]> = {
  owner: ['read', 'comment', 'review', 'edit', 'publish', 'icons', 'team'],
  publisher: ['read', 'comment', 'review', 'edit', 'publish', 'icons'],
  writer: ['read', 'comment', 'review', 'edit'],
  reviewer: ['read', 'comment', 'review'],
  iconDesigner: ['read', 'icons'],
  viewer: ['read'],
};

export function contentRoleCan(role: ContentTeamRole, act: ContentAct): boolean {
  return ACTS[role]?.includes(act) ?? false;
}

/**
 * **A MACHINE LOGIN IS NEVER A PERSON** (Evan, 2026-09-28). An Auth0 Machine-to-Machine token's subject is
 * `<client id>@clients`; a subject like that in the permanent-owner list is a script's credential, so it is never
 * listed on the Team page as an Owner and never counted as a second person who can publish.
 */
export const isMachineSub = (sub: string): boolean => /@clients$/.test(sub);
export const peopleOnly = (subs: readonly string[]): string[] =>
  subs.filter((s) => !isMachineSub(s));

/** Owner and Publisher are company accounts only; everyone else can be anyone. */
export const COMPANY_ONLY_ROLES: readonly ContentTeamRole[] = ['owner', 'publisher'];

export function roleAllowedFor(role: ContentTeamRole, email: string): boolean {
  return !COMPANY_ONLY_ROLES.includes(role) || isCompanyEmail(email);
}

/** The one scope there is: all Kredit Academy content. */
export const CONTENT_SCOPES = ['kreditAcademy'] as const;
export type ContentScope = (typeof CONTENT_SCOPES)[number];

/**
 * **TODAY'S EDITORS** (Evan, 2026-09-26): Publisher for a company account, Writer for anyone else. A row still stored
 * as `editor` is read this way until the migration writes it.
 */
export function roleFromLegacy(
  role: string | null | undefined,
  email: string,
): ContentTeamRole | null {
  if (role === 'editor') return isCompanyEmail(email) ? 'publisher' : 'writer';
  return (CONTENT_TEAM_ROLES as readonly string[]).includes(role ?? '')
    ? (role as ContentTeamRole)
    : null;
}

/** Where the company's days start and end: an end date is a day here. */
export const COMPANY_TIME_ZONE = 'America/New_York';

/** "An owner can set an optional end date; access stops that day" — from its first moment, company time. */
export function accessEnded(endsOn: ISODate | null | undefined, now: Date): boolean {
  return !!endsOn && isoDateIn(now, COMPANY_TIME_ZONE) >= endsOn;
}

/** Today, company time: the earliest end date an owner can set (it stops access at once). */
export const companyToday = (now: Date): ISODate => isoDateIn(now, COMPANY_TIME_ZONE);

/**
 * **A SIGN-OFF, NOT AN EDIT**: an update that only sets scripts' `reviewed` flags. A Reviewer may send exactly this;
 * anything else in the body — a word, a beat, a title — is an edit.
 */
export function isReviewOnly(input: Record<string, unknown>): boolean {
  const keys = Object.keys(input).filter((k) => k !== 'id' && input[k] !== undefined);
  if (keys.length !== 1 || keys[0] !== 'scripts') return false;
  const scripts = input.scripts as Record<string, Record<string, unknown> | undefined> | null;
  if (!scripts || typeof scripts !== 'object') return false;
  return Object.values(scripts).every(
    (band) => !band || Object.keys(band).every((k) => k === 'reviewed' || band[k] === undefined),
  );
}

/** Which act a mission status change is: going live or coming off the trail is a publish; anything else an edit. */
export const statusAct = (status: string): ContentAct =>
  status === 'live' || status === 'retired' ? 'publish' : 'edit';

/**
 * **TWO PEOPLE TO PUT ANYTHING LIVE** (Evan, 2026-09-26): the author of a change can't publish it; another Publisher does.
 * The rule turns on by itself once two people can publish. Until then a Publisher can publish their own change, and
 * the audit line carries this note so the log says so.
 */
export const SOLO_PUBLISH_NOTE = domainCopy().contentTeam.soloPublishNote;
/** `SOLO_PUBLISH_NOTE` in a language. */
export const soloPublishNote = (language?: Language): string => domainCopy(language).contentTeam.soloPublishNote;

/** Whether the two-person rule applies: at least two people who can publish (Owners and Publishers). */
export const twoPersonRuleOn = (publishers: number): boolean => publishers >= 2;

/** "The lock frees after 15 minutes without typing." */
export const EDIT_LOCK_MINUTES = 15;

/**
 * **A comment is pinned to a line** that exists: `title`, `concept`, a script (`scripts.reader`, `scripts.prereader`),
 * a beat's words (`beats.N.prompt`, `beats.N.spoken`, and C13's question after it, `beats.N.ask`), a choice
 * (`beats.N.choices.A.label`, `…consequence`) or a quiz item's words (`quiz.N.prompt`, and C14's `quiz.N.spoken` and
 * `quiz.N.instruction` — the mobile session's three, 2026-09-27). Anything else names no line. The same rule decides a
 * Family Voice line key, so a line a comment can be pinned to is a line a family voice may read.
 */
export function anchorNamesALine(
  anchor: string,
  mission: {
    beats?: ReadonlyArray<{ choices?: ReadonlyArray<{ key: string }> | null }> | null;
    quiz?: ReadonlyArray<unknown> | null;
  },
): boolean {
  if (
    anchor === 'title' ||
    anchor === 'concept' ||
    anchor === 'scripts.reader' ||
    anchor === 'scripts.prereader'
  )
    return true;
  const beat = /^beats\.(\d+)\.(prompt|spoken|ask)$/.exec(anchor);
  if (beat) return Number(beat[1]) < (mission.beats?.length ?? 0);
  const choice = /^beats\.(\d+)\.choices\.([AB])\.(label|consequence)$/.exec(anchor);
  if (choice)
    return !!mission.beats?.[Number(choice[1])]?.choices?.some((c) => c.key === choice[2]);
  const quiz = /^quiz\.(\d+)\.(prompt|spoken|instruction)$/.exec(anchor);
  if (quiz) return Number(quiz[1]) < (mission.quiz?.length ?? 0);
  return false;
}
